import React, { useEffect, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import _isEqual from 'lodash/isEqual';

import { queryStrings } from '@trqk/resolvers';
import { detectionIsOn, locale } from '@trqk/utils';

import { WebLayout } from '../../../templates';
import { Detections, EmptyState, WorkDetailHeader, Performances } from '.';
import { useStatementSelectionForWorkDetail } from '../hooks';
import { GET_MERGED_TRACK_DATA } from '../queries';
import { StatementSelector } from '../../../menus';
import { usePaymentPlanStatus } from '../../../../hooks';

const { GET_SESSION_CURRENT_USER } = queryStrings;

const GET_TRQK_ENTITY = gql`
    query getTrqkEntity($user_id: ID!, $id: ID!) {
        getTrqkEntity(user_id: $user_id, id: $id) {
            id
            name
            pro
            role_type
        }
    }
`;

interface WorkDetailChildProps {
    loadingTrqkAudio: boolean;
    loadingTrqkSong: boolean;
    /**
     * Method called when a new trqkAudio is created
     */
    onCreateTrqkAudio(trqkAudio: TrqkAudio | null): void;
    /**
     * Method called when a trqkAudio is updated to unmatch the work by providing a input with
     * its `song_id` set to `null`.
     */
    onUnmatchWork(): void;
    /**
     * The currently selected list of statement IDs in the StatementSelector. This is initially
     * set to the most recent domestic statement, if available, or the most recent international
     * statement if it exists.
     */
    selectedStatementIds: string[];
    trqkAudio: TrqkAudio | null;
    trqkSong: TrqkSong | null;
}

/**
 * Renders a detailed view of a work.
 *
 * Note that the statement selection context within WorkDetail is separate from the statement
 * selection context used elsewhere in the app.
 */
export const WorkDetailChild = ({
    loadingTrqkAudio,
    loadingTrqkSong,
    onCreateTrqkAudio,
    onUnmatchWork,
    selectedStatementIds,
    trqkAudio,
    trqkSong,
}: WorkDetailChildProps): JSX.Element => {
    const { isPublisherEdition } = usePaymentPlanStatus();
    const trqkAudioId = trqkAudio?.id ?? null;
    const trqkSongId = trqkSong?.id ?? null;
    const { data: currentUserData } = useQuery<{ session: Session }>(
        GET_SESSION_CURRENT_USER,
    );
    const userId = currentUserData?.session?.currentUser?.id ?? null;
    const entityId = trqkSong?.entity_id ?? null;

    const [getTrqkEntity, getTrqkEntityQuery] = useLazyQuery<{
        getTrqkEntity: TrqkEntity;
    }>(GET_TRQK_ENTITY);

    useEffect(() => {
        if (entityId && userId) {
            getTrqkEntity({
                variables: {
                    user_id: userId,
                    id: entityId,
                },
            });
        }
    }, [entityId, userId]);

    const trqkEntity = getTrqkEntityQuery?.data?.getTrqkEntity ?? null;
    const pro = trqkEntity?.pro ?? locale.enUS.UNKNOWN_PRO;

    const [refreshTable, setRefreshTable] = useState(0);

    const [trackData, setTrackData] = useState<ProTrack | null>(null);
    const { statementSelectionProps } = useStatementSelectionForWorkDetail();
    const { currentSelectionSet } = statementSelectionProps;

    const [getMergedTrackData, getMergedTrackDataQuery] = useLazyQuery(
        GET_MERGED_TRACK_DATA,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: (data): void => {
                const nextTrackData = data?.getMergedTrackData ?? null;

                setTrackData(nextTrackData);
                setRefreshTable(Date.now());
            },
        },
    );

    const [lastFetchedIds, setLastFetchedIds] =
        useState<string[]>(selectedStatementIds);

    const selectedIdsString = JSON.stringify(selectedStatementIds);
    const lastFetchedIdsString = JSON.stringify(lastFetchedIds);

    useEffect(() => {
        // When StatementSelector changes the statement selection, call getMergedTrackData with
        // the newly selected statement IDs
        const selectedIdsFromStatementSelector = JSON.parse(selectedIdsString);
        const lastFetchedIdsList = JSON.parse(lastFetchedIdsString);

        if (!_isEqual(selectedIdsFromStatementSelector, lastFetchedIdsList)) {
            getMergedTrackData({
                variables: {
                    song_id: trqkSongId,
                    statement_ids: selectedIdsFromStatementSelector,
                },
            });

            setLastFetchedIds(selectedIdsFromStatementSelector);
        }
    }, [selectedIdsString, lastFetchedIdsString, trqkSongId]);

    /* Gather props for rendering */

    const total_earnings = trackData?.earnings ?? 0;
    const totalPerformances = trackData?.plays ?? 0;
    const detectionIsEnabled = detectionIsOn(
        trqkAudio?.detection_status ?? null,
    );
    const showPerformances = trackData !== null;
    const isLoadingTrackData =
        trqkSongId !== null &&
        (!getMergedTrackDataQuery.called || getMergedTrackDataQuery.loading);

    const loading = loadingTrqkAudio || loadingTrqkSong || isLoadingTrackData;

    return (
        <WebLayout loading={loading}>
            <div className="w-full">
                <WorkDetailHeader
                    entity={trqkEntity}
                    detectionIsOn={detectionIsEnabled}
                    onCreateTrqkAudio={onCreateTrqkAudio}
                    onUnmatchWork={onUnmatchWork}
                    trackData={trackData}
                    trqkAudio={trqkAudio}
                    trqkSong={trqkSong}
                />
                {trqkSong !== null && (
                    <div className="flex items-center my-6">
                        <h5 className="mr-3">Performances from:</h5>
                        <StatementSelector
                            statementSelectionProps={statementSelectionProps}
                        />
                    </div>
                )}
                {showPerformances && (
                    <Performances
                        periodLabel={currentSelectionSet.label.long}
                        pro={pro}
                        refreshTable={refreshTable}
                        total_earnings={total_earnings}
                        totalPerformances={totalPerformances}
                        trackData={trackData}
                    />
                )}
                {!isPublisherEdition && detectionIsEnabled && trqkAudioId && (
                    <Detections trqkAudioId={trqkAudioId} />
                )}
                {!isPublisherEdition && !showPerformances && (
                    <EmptyState
                        detectionIsEnabled={detectionIsEnabled}
                        hasTrqkAudio={trqkAudio !== null}
                        hasTrqkSong={trqkSong !== null}
                    />
                )}
            </div>
        </WebLayout>
    );
};
